import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/** What the visitor chose. `auto` defers to the OS/browser setting and tracks it live. */
export type ThemePreference = 'light' | 'auto' | 'dark';

/** What that choice actually resolves to right now — the value written to `data-theme`. */
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'pf-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'auto' || value === 'dark';

/**
 * The stored preference, or `auto` when nothing was chosen (or storage is unavailable —
 * Safari private browsing throws on access rather than returning null).
 */
export const readStoredPreference = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
};

const systemTheme = (): ResolvedTheme =>
  typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';

export const resolveTheme = (preference: ThemePreference): ResolvedTheme =>
  preference === 'auto' ? systemTheme() : preference;

interface ThemeContextValue {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  readonly setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Owns the theme for the whole app: persists the visitor's choice, keeps `auto` in sync with
 * the browser as it changes, and mirrors the resolved value onto <html data-theme> — the single
 * hook every stylesheet keys off (see src/styles/index.css).
 *
 * The same attribute is written by an inline script in public/index.html before first paint, so
 * this provider's first effect is normally a no-op rather than a visible correction.
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readStoredPreference()));

  // Follow the OS while on `auto`. Bound unconditionally and gated inside, so flipping the
  // system setting while on an explicit choice and *then* switching to auto still lands correctly.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const sync = () => setResolved(resolveTheme(preference));
    sync();

    if (preference !== 'auto') return;
    // Safari < 14 only has the deprecated addListener.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', sync);
      return () => query.removeEventListener('change', sync);
    }
    query.addListener(sync);
    return () => query.removeListener(sync);
  }, [preference]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    setResolved(resolveTheme(next));
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage blocked (private mode, cookies off) — the choice still applies for this session.
    }
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
